import { useCallback, useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { parse } from 'querystring';
import { useFormInputAccountPassword, useFormInputEmail } from 'src/components/FormInput';
import { ACCOUNT_PASSWORD_CONFIRM_VALIDATE_ERROR } from 'src/constants/validate';
import Button from 'src/components/Button';
import { userPasswordChange, userPasswordChangeVerification } from 'src/services/api';
import { useTranslation } from 'react-i18next';
import useCustomHistory from 'src/hooks/useCustomHistory';
import { useModal } from 'src/ModalContext';

export default function PasswordChange(): JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useCustomHistory();
  const { showModal } = useModal();
  const { email, code } = parse(location.search.replace(/^[?]/gi, ''));
  const emailInput = useFormInputEmail({
    inputAttr: {
      disabled: true,
    },
  });
  const passwordInput = useFormInputAccountPassword({
    label: t('password/change/password_button'),
  });
  const passwordConfirmInput = useFormInputAccountPassword({
    label: t('password/change/password_confirm_button'),
    name: 'passwordConfirm',
    validate: {
      required: (value: string) => !!value,
      match: (value: string) => passwordInput.value === value,
    },
    errors: ACCOUNT_PASSWORD_CONFIRM_VALIDATE_ERROR,
  });

  useEffect(() => {
    if (passwordInput.value !== '' && passwordConfirmInput.value !== '') {
      passwordConfirmInput.triggerValidate();
      passwordInput.triggerValidate();
    }
  }, [passwordInput.value, passwordConfirmInput.value]);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      userPasswordChange({
        email: e.target.email.value,
        code: e.target.code.value,
        password: e.target.password.value,
      })
        .then(() => {
          showModal({
            message: t('password/change/complete_alert'),
            success: () => {
              history.push('/login');
            },
          });
        })
        .catch((e) => {
          showModal({
            message: e.message,
            success: () => {
              return;
            },
          });
        });
    },
    [history],
  );

  useLayoutEffect(() => {
    userPasswordChangeVerification({
      code,
      email,
    } as ApiUserPasswordChangeVerificationParams)
      .then(() => {
        emailInput.setValue(email as string);
      })
      .catch(() => {
        history.replace('/password/expire');
      });
  }, []);

  return (
    <>
      <form onSubmit={onSubmit}>
        <div className="container mb-10 mt-6">
          <h1 className="N_20_R">{t('password/change/title')}</h1>
          <div className="mt-7">{emailInput.CustomFormInput}</div>
          <div className="mt-5">{passwordInput.CustomFormInput}</div>
          <div className="mt-5">{passwordConfirmInput.CustomFormInput}</div>
          <div className="mt-6">
            <input type="hidden" name="code" value={code} />
            <Button
              title={t('password/change/change_button')}
              type="submit"
              disabled={!(passwordInput.isValid && passwordConfirmInput.isValid)}
            ></Button>
          </div>
        </div>
      </form>
    </>
  );
}
